/**
 * ARENA PVE — KIỂU DỮ LIỆU. LOGIC THUẦN: 0 dòng import Phaser (luật kiến trúc README).
 *
 * VÌ SAO TÁCH HẲN KHỎI `sim/types.ts` + `sim/world.ts`:
 *   · `World` cũ là màn THỦ NHÀ dọc (1000×600, 1 Nhà, 1 làn) — Arena là màn NGANG với Nhà của CẢ
 *     HAI phe, SÔNG dọc chia 2 bờ và 2 CẦU. Nhét chung vào `World` là kéo theo hàng trăm nhánh `if`
 *     trong vòng lặp 30Hz vốn đã 2715 dòng, và mọi test cũ phải chạy lại đường mới.
 *   · Tài nguyên của Arena là VÍ RIÊNG (`resource`), KHÔNG phải `gold` của game TD. Trộn hai ví
 *     là hỏng luôn cân bằng kinh tế TD đang chạy ổn.
 *   · Arena cần chạy headless nghìn trận/giây ⇒ giữ được vòng lặp gọn, không kéo art/ skill/ ult.
 *
 * ⚠️ HỢP ĐỒNG API: chữ ký trong file này + `sim/arena/world.ts` do Lead chốt và các teammate
 * T-B/ T-C/ T-D code theo. Đổi chữ ký PHẢI báo Lead trước, đừng "sửa cho tiện".
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ `Land` (`0 | 1 | 2`) ĐÃ BỊ XOÁ KHỎI FILE NÀY — ĐỪNG "KHÔI PHỤC LẠI CHO ĐỦ BỘ"
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Bản trước chia sân thành 3 LAND (3 dải ngang), và mọi unit bị KHOÁ vào land của nó ⇒ "3 land" là
 * 3 MẶT TRẬN ĐỘC LẬP chứ không chỉ là 3 hàng để vẽ.
 *
 * Người dùng đã chốt lại luật: sân nay là **không gian 2 CHIỀU tự do**, có SÔNG dọc chia 2 bờ và
 * **2 CẦU** là cửa duy nhất qua sông (*"quái và nhân vật chỉ có thể đi qua cầu và không đi được qua
 * sông"*), và người chơi **thả tướng ở toạ độ tự chọn** trong nửa sân của mình (*"thả sai thì huỷ,
 * báo lỗi"*). Với luật đó, `land` không còn nghĩa gì: không có dải nào để khoá unit, không có
 * "chọn land" để bấm.
 *
 * ⚠️ HỆ QUẢ: mọi chỗ đọc `u.land`/ `m.land` phải đổi sang TOẠ ĐỘ THẬT (`u.x`/ `u.y`) — và đó không
 * phải đổi tên suông: `pickTarget` cũ lọc `t.land === u.land` (chỉ đánh trong cùng land), `moveToward`
 * cũ bỏ thành phần y khi đích khác land. Cả hai luật đó đã bị XOÁ, không phải "dịch" (xem `world.ts`).
 * Khai lại `Land` ở đây sẽ làm code cũ biên dịch được trong khi luật đã khác — đúng loại lỗi im lặng
 * mà cả dự án đang tránh.
 */
import type { ClassId, Stats, UnitState } from '../types';

/**
 * PHE — TÁI DÙNG ĐÚNG KHÁI NIỆM `Team` của `sim/types.ts` ('hero' | 'enemy').
 *
 * ⚠️⚠️ ĐÂY LÀ SỬA HỢP ĐỒNG, KHÔNG PHẢI ĐẶT TÊN CHO VUI — BẪY ĐÃ DÍNH.
 *
 * Bản nháp đầu của task này cho `ArenaUnit.side: 'left' | 'right'` (chỉ 10 trường). Lead đã
 * `grep` `src/render/unitview.ts` và chỉ ra hệ quả: `UnitViews.sync()` lặp `world.units` rồi đọc
 * 25 trường của `Unit`, và `buildView()` rẽ nhánh theo **`u.team === 'hero'`** để chọn rig tướng
 * hay rig quái. Với `side` riêng, T-C **không dùng lại được** `UnitViews` (~25 KB code đã có test)
 * mà phải viết lại toàn bộ lớp vẽ nhân vật.
 *
 * ⇒ `ArenaUnit` mang `team: Team` ĐÚNG TÊN TRƯỜNG của `Unit` (để `buildView` rẽ nhánh y hệt),
 *   còn `side` SUY RA từ `team` (hero→left, enemy→right) và giữ lại vì code gameplay đọc `side`
 *   dễ hiểu hơn `team`. PVE: người chơi LUÔN `left`. (PVP để dự án sau — task này KHÔNG làm.)
 */
export type Team = 'hero' | 'enemy';
export type ArenaSide = 'left' | 'right';

/** Suy `side` từ `team`. Một chiều, không bao giờ ngược lại — xem chú thích ở `Team`. */
export function sideOfTeam(team: Team): ArenaSide {
  return team === 'hero' ? 'left' : 'right';
}

export type ArenaStatus = 'prep' | 'fighting' | 'won' | 'lost' | 'draw';

/** Chế độ. Chỉ có `pve` trong task này; khai kiểu riêng để đổi sang `pvp` không phá chữ ký. */
export type ArenaPhase = 'pve';

/**
 * MỘT ĐƠN VỊ TRONG ARENA — tướng · lính · boss · quái khủng · nông dân dùng CHUNG kiểu này.
 *
 * ⚠️ 25 TRƯỜNG DƯỚI ĐÂY LÀ HỢP ĐỒNG VỚI `src/render/unitview.ts`, KHÔNG PHẢI "CHO ĐỦ".
 * `UnitViews` đọc thẳng: `alive · attackMove · base · castAnim · castDur · castT0 · deadAt ·
 * facing · hitFlash · hp · id · isBoss · isClone · isConverted · isElite · kind · mana · radius ·
 * state · swing · team · ultUntil · x · y` (+ `base.hp`, `base.mana`). Xoá một trường là T-C vỡ
 * ở tầng vẽ — mà lỗi đó chỉ lộ ra khi chạy game thật, không lộ trong unit test của sim.
 *
 * Vì sao KHÔNG tái dùng thẳng `Unit`: `Unit` còn 15 trường khác (stamina · ammo · shield · buff ·
 * skillCds · targetId · minions · orderX/Y…) chỉ có nghĩa với màn TD dọc. Arena bản này chỉ đánh
 * thường nên giữ ĐÚNG tập mà lớp vẽ cần + trường riêng của Arena (`carrying`, `mineIndex`).
 * Khi Arena cần chiêu thức thì MỞ RỘNG kiểu này, đừng đổi sang `Unit`.
 *
 * ⚠️ `kind` LÀ HAI THỨ KHÁC NHAU tuỳ `isHero`:
 *   · tướng   ⇒ `kind` = `ClassId` (khoá tra `CLASSES`)
 *   · còn lại ⇒ `kind` = id quái trong `ENEMIES` / id riêng (xem `ARENA_UNIT_KIND`)
 * Đừng tra `CLASSES[u.kind]` mà không kiểm `isHero` — id class và id quái không trùng nhau nên
 * tra bừa chỉ ra `undefined` (đúng cái `buildView` phải chống bằng `u.team === 'hero'`).
 */
export interface ArenaUnit {
  id: number;
  /** phe — TÊN TRƯỜNG PHẢI LÀ `team`, xem chú thích ở `Team`. */
  team: Team;
  /** phe sở hữu, suy ra từ `team` (hero→left, enemy→right). Đọc cho rõ nghĩa trong code gameplay. */
  readonly side: ArenaSide;
  /**
   * GÓC RẢI ĐIỂM ĐẾN (radian) — gán MỘT LẦN lúc sinh đơn vị, từ `rng` CỦA CHÍNH WORLD.
   *
   * ⚠️⚠️ VÌ SAO KHÔNG DÙNG `u.id`: `id` lấy từ biến đếm TOÀN CỤC (`nextArenaId`) DÙNG CHUNG cho mọi
   * `ArenaWorld` trong cùng tiến trình. Bản đầu tôi rải điểm đến bằng `u.id * 2,399963` ⇒ hai world
   * CÙNG SEED nhưng tạo ở thời điểm khác nhau nhận `id` khác nhau ⇒ hành vi lệch nhau, và bài
   * "tính TẤT ĐỊNH: cùng seed + cùng lệnh ⇒ cùng kết quả" ĐỎ. Dùng `rng` của world thì cùng seed ⇒
   * cùng góc, độc lập với thứ tự tạo world.
   *
   * `spread` còn là nửa còn lại của BIỆN PHÁP CHỐNG ÙN TẮC: mỗi unit nhắm một điểm LỆCH quanh đích
   * (xem `moveToward`/ `crossingY` trong `world.ts`) nên chúng không xếp thành một hàng dọc.
   */
  spread: number;
  /**
   * ĐỒNG HỒ CHỜ ĐỂ NIỆM **CHIÊU ĐÃ BỐC SẴN** (`nextSkillId`) — giây CÒN LẠI. `0` = niệm được ngay.
   *
   * ⚠️⚠️ MỘT ĐỒNG HỒ DUY NHẤT CHO MỘT TƯỚNG (yêu cầu người dùng 02/10/2026). Luật auto-xả chiêu:
   *   1. Ra sân ⇒ **bốc sẵn 1 chiêu** trong số chiêu Arena dùng được (`nextSkillId`);
   *   2. `ARENA_SKILL_FIRST_DELAY` giây đầu KHÔNG niệm (nay **2s** — xem `makeHeroUnit`);
   *   3. niệm xong chiêu đó ⇒ **bốc sẵn chiêu kế tiếp ngay** (random lại), và đồng hồ chờ được đặt
   *      theo **chiêu VỪA niệm** (`arenaSkillCooldown(chiêu vừa niệm)` — chiêu càng tốn mana thì
   *      chờ càng lâu).
   *
   * ⚠️ VÌ SAO BỎ "HỒI CHIÊU THEO TỪNG CHIÊU" (bản cũ có `skillCds[id]`): hồi chiêu riêng làm NHIỀU
   * chiêu cùng sẵn sàng một lúc, nên tướng xả 2–3 chiêu liên tiếp trong ~1 giây (chỉ bị chặn bởi nhịp
   * nghỉ 0,6s) — đúng hiện tượng người dùng báo *"đẻ mướn tôi thấy có khi dùng 2-3 skill liên tục"*.
   * Một đồng hồ duy nhất khiến "một chiêu rồi chờ" là bất biến của sim.
   */
  skillCd: number;
  /**
   * CHIÊU ĐÃ BỐC SẴN, SẼ NIỆM KHI HẾT `skillCd` — id chiêu trong `arenaCastSkills(kind)`.
   *
   * `null` = đơn vị không có chiêu nào để bốc (lính/ quái/ nông dân) hoặc class không có chiêu.
   * Đây là lựa chọn TẤT ĐỊNH theo `rng` của world: cùng seed ⇒ cùng chuỗi chiêu (xem `rollArenaSkill`).
   */
  nextSkillId: string | null;
  /**
   * THỜI ĐIỂM (giây sim) TUYỆT KỸ BẮT ĐẦU ĐƯỢC BỐC — `thời điểm ra sân + ARENA_ULT_FIRST_AT`.
   *
   * ⚠️ Giữ đúng bài học cũ *"strength 1 skill gần chết luôn tướng đối thủ"*: trước mốc này
   * `rollArenaSkill` CHỈ bốc chiêu thường (`cooldown < ARENA_ULT_COOLDOWN_MIN`), nên pha đầu trận
   * không thể mở màn bằng một đòn hạ sát.
   */
  ultReadyAt: number;
  /**
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * ULTIMATE CỦA CLASS (chiêu thứ 8) — DÙNG TỰ ĐỘNG TRONG ARENA
   * ════════════════════════════════════════════════════════════════════════════════════════════
   * Người dùng 02/10/2026: *"đến phút thứ 3 … tất cả các tướng dùng skill ult (giống bên vượt ải,
   * có đủ hiệu ứng skill như vượt ải)"* + *"countdown 60s"*.
   *
   * ⚠️ `ultReadyAt` Ở TRÊN LÀ CHUYỆN KHÁC — ĐỪNG GỘP: nó là mốc để `rollArenaSkill` cho phép bốc các
   * chiêu THƯỜNG có hồi dài (`cooldown ≥ 30`). Ultimate class là hệ riêng, có `classUltReadyAt`.
   *
   * `ultUntil` = mốc hết hiệu lực (0 = không dùng). Nó vừa là LUẬT (hết hạn thì dọn hiệu ứng), vừa là
   * dữ liệu để lớp vẽ đổi dáng biến hình (`ULT_FORMS` — Dã trư khổng lồ của Dân tập gym).
   */
  classUltReadyAt: number;
  /** mốc hết hiệu lực của ultimate class (giây sim); `0` = không có hiệu lực nào đang chạy. */
  ultUntil: number;
  /** BỊ CHOÁNG tới mốc này (tiếng gầm của Dã trư) — đơn vị bị choáng bỏ lượt hành động. */
  stunUntil: number;
  /** MỐC HẾT BỊ THIÊU ĐỐT (bom xăng) — mỗi tick mất `burnDps`; `0` = không cháy. */
  burnUntil: number;
  /** sát thương/ giây của vết cháy (chỉ có nghĩa khi `burnUntil > time`). */
  burnDps: number;
  /** MỐC HẾT BỊ GIẢM GIÁP (bom xăng: ×0,6) — xem `hitUnit`/ `hitUnitWith`. */
  defShredUntil: number;
  /**
   * CHIÊU VỪA NIỆM + thời điểm niệm — **DỮ LIỆU CHẾT CHO TẦNG HIỂN THỊ**, không tham gia luật.
   *
   * Người dùng: *"Khi bốc skill hãy hiển thị tên skill được bốc ở đâu đó hợp lý"*. HUD đọc 2 trường này
   * để hiện tên chiêu trong vài giây sau khi niệm; KHÔNG dòng luật nào đọc chúng (không tính sát thương,
   * không chọn mục tiêu) nên chúng không thể làm lệch mô phỏng — cùng quy ước với `ArenaShot`/ `castFx`.
   */
  lastCastId: string | null;
  /** thời điểm (giây sim) của `lastCastId`; `0` = chưa từng niệm */
  lastCastAt: number;
  /**
   * HẾT HẠN CỦA QUÂN TRIỆU HỒI (giây sim) — `0` = sống mãi (lính/ boss/ khủng/ tướng/ nông dân).
   *
   * ⚠️ Chỉ `summon` mới đặt trường này (xem `ArenaWorld.summonFrom`): đệ tử của "Đẻ mướn" phải tự tan
   * sau `skill.duration`, nếu không thì mỗi lần niệm là cộng thêm quân VĨNH VIỄN ⇒ trận nào cũng thành
   * "đẻ 12 đứa rồi thắng bằng số đông", và bộ nhớ unit phình theo thời gian.
   */
  expireAt: number;
  kind: string;
  isHero: boolean;
  isFarmer: boolean;
  isBoss: boolean;
  isElite: boolean;
  isTitan: boolean;
  isClone: boolean;
  isConverted: boolean;
  x: number;
  y: number;
  radius: number;
  /** chỉ số gốc — `unitview` đọc `base.hp`/ `base.mana` để vẽ thanh máu/ mana. */
  base: Stats;
  hp: number;
  /** máu tối đa. Trùng `base.hp` (xem `ArenaWorld.spawnUnit`) nhưng giữ riêng để render khỏi suy diễn. */
  maxHp: number;
  mana: number;
  alive: boolean;
  /** `1` = nhìn sang phải (đi tấn công), `-1` = nhìn sang trái. */
  facing: 1 | -1;
  state: UnitState;
  /** 0..1 — tiến trình vung đòn, render dùng để vung tay. */
  swing: number;
  /** thế võ đang ra (index vào combo của `kind`, xem `data/combat-moves.ts`) — 0 là mặc định. */
  attackMove: number;
  /** dáng ra chiêu đang diễn (`beginCast` đặt, hết `ARENA_CAST_TIME` thì về `null`) — `unitview` ĐỌC nó. */
  castAnim: string | null;
  castT0: number;
  castDur: number;
  hitFlash: number;
  deadAt: number;
  /**
   * GIÂY CÒN LẠI TỚI ĐÒN KẾ TIẾP.
   *
   * Trường NỘI BỘ của mô phỏng (`unitview` KHÔNG đọc nó — bản gốc `Unit` cũng có `attackCd` nhưng
   * lớp vẽ suy nhịp vung từ `swing`). Vì sao vẫn để trong `ArenaUnit` chứ không giấu trong một
   * `Map` riêng: `updateFighter` chạy mỗi tick cho mọi unit, mà tra `Map` theo id mỗi tick là cấp
   * phát + băm thừa. Arena chạy nghìn trận headless nên trường thẳng trên object là lựa chọn có ý.
   */
  attackCd: number;
  /** tài nguyên nông dân đang vác trên lưng (0 với mọi unit khác) */
  carrying: number;
  /**
   * SỐ GIÂY NÔNG DÂN ĐỨNG YÊN DÙ ĐANG Ở TRẠNG THÁI ĐI (`state === 'move'`) — VAN CHỐNG KẸT GIAO HÀNG.
   *
   * ⚠️ VÌ SAO CẦN (đo được, seed 1001): nông dân vác ĐẦY bao (30) bị `separateUnits` chèn giữa bãi quân
   * quanh Nhà ⇒ đứng nguyên **18 giây** mà `state` vẫn là `'move'` ⇒ kinh tế đứng im mà không có lỗi nào.
   * Cùng loại lỗi "chết câm lặng" mà nhóm test task-9 phải chặn. Khi `stuckTime` vượt
   * `ARENA_FARMER_STUCK_DELIVER`, `updateFarmerDelivery` cho nông dân XẢ HÀNG tại chỗ (xem chú thích ở đó).
   *
   * ⚠️ ĐO SAU `separateUnits`, KHÔNG đo bên trong `updateFarmer`: trong `updateFarmer` nông dân VẪN đi
   * được một bước (rồi bị va chạm đẩy ngược lại SAU đó) ⇒ đo ở trong sẽ luôn thấy "đang đi" và van
   * không bao giờ kích hoạt (đúng cái bẫy đã sập lần đầu).
   *
   * ⚠️ `0` với MỌI đơn vị không phải nông dân — trường này chỉ có nghĩa trong `updateFarmerDelivery`.
   */
  stuckTime: number;
  /** TOẠ ĐỘ THAM CHIẾU của lần đo trước — chỉ dùng cho van chống kẹt ở trên (0 với mọi unit khác). */
  stuckRefX: number;
  stuckRefY: number;
  /**
   * BÊN ĐANG LÁCH ĐỂ NÉ VẬT CẢN (`1`/`-1`), `0` = không lách — XEM `moveToward`.
   *
   * ⚠️⚠️ VÌ SAO PHẢI NHỚ BÊN: nếu mỗi tick chọn lại theo dấu `cross` của vật cản, một vật cản nằm gần
   * CHÍNH GIỮA sẽ làm dấu đó đổi liên tục (nhiễu số học) ⇒ quân LẮC QUA LẠI từng tick thay vì lách
   * một mạch. Đo được: bài "KHÔNG DAO ĐỘNG" (`tests/arena-river.test.ts`) bắt ngay số lần đổi dấu `Δy`.
   * Nhớ bên ⇒ tối đa MỘT lần đổi bên cho mỗi lần gặp vật cản.
   *
   * ⚠️ `0` với mọi đơn vị không đi lách (lính đứng, nông dân, quái khủng quá to để lách trên cầu).
   */
  dodgeSide: 1 | -1 | 0;
  /** ĐỒNG HỒ GIỮ BÊN LÁCH (giây còn lại) — xem `moveToward`/ `ARENA_DODGE_HOLD` (0 với unit khác). */
  dodgeHold: number;
  /** mine nông dân đang nhắm — khoá tra `ArenaWorld.mines`, `null` khi không nhắm mine nào */
  mineIndex: number | null;
  /**
   * ĐANG KHOÁ MỤC TIÊU ĐÁNH TRỤ — một khi đã bắt đầu đánh Nhà, tiếp tục đánh tới khi
   * Nhà nổ hoặc bản thân bị hạ gục. Chỉ ngắt khoá khi bị Stun/Freeze, bị đẩy văng khỏi tầm,
   * hoặc bị chèn người (body-block).
   */
  lockedOnCore: boolean;
}

/**
 * MỎ TÀI NGUYÊN.
 *
 * ⚠️ `land` ĐÃ BỊ XOÁ — mỏ nay chỉ có TOẠ ĐỘ. Bản trước gắn `land` vào mỏ vì unit bị khoá theo land
 * (nông dân chỉ đào được mỏ cùng land). Luật đó không còn: nông dân đi tự do 2 chiều và có thể qua
 * CẦU sang bờ bên kia để đào (xem `pickMine` trong `world.ts`).
 *
 * 2 mỏ đặt ở 2 GÓC map (trên/ dưới, thẳng cột với Nhà) — đúng yêu cầu "2 MINE ở 2 góc map" và
 * *"Mine mà nông dân nhặt để thẳng cột với nhà và xa nhất có thể trong màn hình"*. `y` của 2 mỏ
 * nay là 2 số CỐ ĐỊNH (không suy từ `ARENA_LANE_Y` — hằng số đó đã bị xoá cùng `land`).
 */
export interface ArenaMine {
  index: number;
  x: number;
  y: number;
  /** trữ lượng còn lại — hết thì nông dân đứng chờ, KHÔNG kẹt vòng lặp (xem `updateFarmer`) */
  amount: number;
  /** `null` = mỏ trung lập. Bản PVE cả 4 mỏ đều trung lập, ai cũng đào được. */
  owner: ArenaSide | null;
}

/** NHÀ CHÍNH. Máu = điều kiện thắng (phá Nhà địch) + cơ sở tính điểm khi hết giờ. */
export interface ArenaCore {
  side: ArenaSide;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  radius: number;
}

export interface ArenaSettings {
  seed?: number;
  phase: ArenaPhase;
  /** class ĐÃ UNLOCK — dùng để validate lệnh summon (không có trong đây ⇒ `ok:false`) */
  heroClasses: ClassId[];
  /** độ dài trận (giây). Mặc định `ARENA_TICK.matchSeconds` = 300 (5 phút). */
  matchSeconds?: number;
}

/**
 * LỆNH NGƯỜI CHƠI GỬI XUỐNG MÔ PHỎNG.
 *
 * Vì sao khai cả interface lẫn việc `ArenaWorld` tự có 2 hàm: UI cầm `ArenaCommand` để gọi qua
 * một điểm duy nhất (dễ chặn/ ghi log/ replay), còn test và công cụ cân bằng gọi thẳng
 * `arena.summonHero(...)`. Không có lớp trung gian nào khác.
 *
 * ⚠️⚠️ API ĐÃ ĐỔI: `summonHero(classId, x, y)` và `buyFarmer(x, y)` — THAY `(classId, land)`.
 * Bản cũ nhận `land` (0|1|2) vì tướng ra ở dải ngang tương ứng. Nay `land` không còn tồn tại:
 * người chơi thả quân ở **TOẠ ĐỘ TỰ CHỌN** trong nửa sân của mình, và toạ độ SAI thì lệnh bị HUỶ
 * (`ok: false` + `message`), **KHÔNG trừ tài nguyên** — người dùng chốt *"thả sai thì huỷ, báo lỗi"*.
 *
 * ⇒ Tầng UI/ scene phải cập nhật theo (họ lo phần đó): `pointer.worldX/ worldY` truyền thẳng xuống,
 * không còn bước "đổi y → land" nào.
 */
export interface ArenaCommand {
  summonHero(classId: ClassId, x: number, y: number): { ok: boolean; message: string };
  buyFarmer(x: number, y: number): { ok: boolean; message: string };
}

/**
 * TÀI NGUYÊN ĐÃ TIÊU + ĐÃ ĐÀO — chỉ để HUD/ cân bằng đọc, KHÔNG ảnh hưởng luật.
 * (`score()` chỉ tính từ máu Nhà — xem `ArenaWorld.score`.)
 */
export interface ArenaTally {
  spent: number;
  mined: number;
}
